import { useState, useEffect, useRef } from 'react';
import './TimeDropdown.css';

const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m of [0, 30]) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      options.push(`${hh}:${mm}`);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const PERIOD_LABEL = (h: number) => {
  if (h === 0) return 'Midnight';
  if (h === 12) return 'Noon';
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  if (h < 21) return 'Evening';
  return 'Night';
};

function TimeDropdown({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (time: string) => void;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedEl = listRef.current.querySelector('.td-option--selected');
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'center' });
      }
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const handleSelect = (time: string) => {
    onChange(time);
    setIsOpen(false);
  };

  return (
    <div className={`td-wrapper ${disabled ? 'td-wrapper--disabled' : ''}`} ref={ref}>
      <button
        type="button"
        className={`td-trigger ${isOpen ? 'td-trigger--open' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
      >
        <i className="fa-regular fa-clock td-icon" />
        <span className="td-value">{value || '—'}</span>
        <i className={`fa-solid fa-chevron-down td-arrow ${isOpen ? 'td-arrow--up' : ''}`} />
      </button>

      {isOpen && (
        <div className="td-dropdown" ref={listRef}>
          {TIME_OPTIONS.map((time) => {
            const h = parseInt(time.split(':')[0], 10);
            const isSelected = time === value;
            const isHourStart = time.endsWith(':00');

            return (
              <div
                key={time}
                className={`td-option${isSelected ? ' td-option--selected' : ''}${isHourStart ? ' td-option--hour' : ''}`}
                onClick={() => handleSelect(time)}
              >
                <span className="td-option-time">{time}</span>
                {isHourStart && <span className="td-option-period">{PERIOD_LABEL(h)}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TimeDropdown;
